import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getPhotoUrl } from "../../../common/utils/getPhotoUrl";
import { getUserInfoByUuids } from "../apis/chatApi";
import { getDisplayName, set as setCachedUser } from "../stores/userDisplayUtils";
import { getCurrentUserId } from "../utils/messageMapping";
import { deriveRoomDisplayName } from "../utils/roomListMapping";

/** 캐시·member.name 모두 없을 때만 UUID로 프로필 조회 대상에 넣음 */
function collectUserIdsToResolve(room, myId) {
  if (!room?.id) return [];
  const members = room.members || [];
  const others = members.filter((m) => m.userId && m.userId !== myId);
  const ids = new Set();
  for (const m of others) {
    const uid = m.userId;
    if (getDisplayName(uid)) continue;
    if (m.name != null && String(m.name).trim() !== "") continue;
    ids.add(uid);
  }
  if (others.length === 0 && room.createdBy && room.createdBy !== myId) {
    const c = room.createdBy;
    if (!getDisplayName(c)) ids.add(c);
  }
  return [...ids];
}

export function useRoomDisplayMeta(room) {
  const myId = getCurrentUserId();

  const roomStableKey = useMemo(() => {
    if (!room?.id) return "";
    const mids = (room.members || []).map((m) => m.userId).filter(Boolean).join(",");
    return `${room.id}:${mids}:${room.createdBy ?? ""}`;
  }, [room?.id, room?.members, room?.createdBy]);

  const needIds = useMemo(
    () => collectUserIdsToResolve(room, myId),
    [room, myId]
  );

  const { dataUpdatedAt } = useQuery({
    queryKey: ["roomUserResolve", roomStableKey],
    queryFn: async () => {
      const users = await getUserInfoByUuids(needIds);
      users.forEach((u) => {
        if (u.uuid) {
          setCachedUser(u.uuid, {
            name: u.name || null,
            photo: getPhotoUrl(u.photo, "agent"),
          });
        }
      });
      return users;
    },
    enabled: needIds.length > 0 && roomStableKey !== "",
    staleTime: Infinity,
  });

  // dataUpdatedAt: 쿼리가 유저 정보를 가져온 뒤 캐시가 갱신되면 재계산
  return useMemo(() => {
    if (!room) return { displayName: "", avatarUrl: null };
    const d = deriveRoomDisplayName(room, myId);
    return {
      displayName: d?.name ?? "",
      avatarUrl: d?.avatarUrl ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, myId, roomStableKey, dataUpdatedAt]);
}
